import { transparentize } from 'polished';
import { setAlpha } from '../../../design-tokens';
import { renderDesignTokens, renderInlineCssVars } from '../../../theme';
import { getCheckboxDesignTokens, getCheckboxThemeTokens } from '../../Checkbox/Checkbox';
import { getRadioButtonDesignTokens, getRadioButtonThemeTokens } from '../../RadioButton/__internal__';
export const getListTokens = (dt, density, theme, font, variant, whiteSpace) => {
  const fontSize = density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m'];
  let tokenValues = {
    ...getCheckboxThemeTokens(dt),
    ...getRadioButtonThemeTokens(dt),
    ...getListBaseThemeTokens(dt, variant),
    ...renderInlineCssVars({
      'squish-s': `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`,
      s: dt.tokens['space-base-s'],
      xs: dt.tokens['space-base-xs'],
      xl: dt.tokens['space-base-xl']
    }, 'list-spacing'),
    ...renderInlineCssVars({
      'font-size-m': dt.enableNovaTokens ? fontSize : dt.reportTokens.font.size.m,
      'font-size-s': dt.enableNovaTokens ? dt.tokens['font-size-body-text-s'] : dt.reportTokens.font.size.s
    }, 'list'),
    ...font,
    ...(!!whiteSpace ? renderInlineCssVars({
      'white-space': whiteSpace
    }, 'list-item') : {}),
    ...renderInlineCssVars({
      'font-weight-thick': dt.tokens['font-style-weight-700']
    }, 'heading')
  };
  const overrides = theme;
  if (overrides) {
    tokenValues = {
      ...tokenValues,
      ...renderInlineCssVars(overrides({
        state: 'normal'
      }), 'list-item'),
      ...renderInlineCssVars(overrides({
        state: 'hover'
      }), 'list-item-hover'),
      ...renderInlineCssVars(overrides({
        state: 'pressed'
      }), 'list-item-active'),
      ...renderInlineCssVars(overrides({
        state: 'selected'
      }), 'list-item-selected'),
      ...renderInlineCssVars(overrides({
        state: 'disabled'
      }), 'list-item-disabled')
    };
  }
  return tokenValues;
};
export const getListDesignTokens = (dt, theme, variant, whiteSpace) => {
  let tokenValues = {
    ...getCheckboxDesignTokens(dt),
    ...getRadioButtonDesignTokens(dt),
    ...getListBaseThemeTokens(dt, variant),
    ...renderDesignTokens({
      'squish-s': `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`,
      s: dt.tokens['space-base-s'],
      xs: dt.tokens['space-base-xs'],
      xl: dt.tokens['space-base-xl']
    }, 'list-spacing'),
    ...renderDesignTokens({
      'font-size-m': dt.enableNovaTokens ? dt.tokens['font-size-body-text-m'] : dt.reportTokens.font.size.m,
      'font-size-s': dt.enableNovaTokens ? dt.tokens['font-size-body-text-s'] : dt.reportTokens.font.size.s
    }, 'list'),
    ...(!!whiteSpace ? renderDesignTokens({
      'white-space': whiteSpace
    }, 'list-item') : {})
  };
  const overrides = theme;
  if (overrides) {
    tokenValues = {
      ...tokenValues,
      ...renderDesignTokens(overrides({
        state: 'normal'
      }) ?? {}, 'list-item'),
      ...renderDesignTokens(overrides({
        state: 'hover'
      }) ?? {}, 'list-item-hover'),
      ...renderDesignTokens(overrides({
        state: 'pressed'
      }) ?? {}, 'list-item-active'),
      ...renderDesignTokens(overrides({
        state: 'selected'
      }) ?? {}, 'list-item-selected'),
      ...renderDesignTokens(overrides({
        state: 'disabled'
      }) ?? {}, 'list-item-disabled')
    };
  }
  return tokenValues;
};
const getListBaseThemeTokens = (dt, variant) => {
  if (dt.enableNovaTokens) {
    let listItemFg, listItemHoverFg, listItemHoverBg, listItemFocusOutline;
    if (variant === 'primary-light' && dt.themeVariant === 'light') {
      listItemFg = dt.tokens['color-text-neutral-inverse'];
      listItemHoverFg = dt.tokens['color-text-neutral-inverse'];
      listItemHoverBg = setAlpha(dt.tokens['color-container-background-0'], 0.1);
      listItemFocusOutline = 'var(--focus-dark-bg)';
    } else {
      listItemFg = dt.tokens['color-body-text'];
      listItemHoverFg = dt.tokens['color-action-text-standard-hover'];
      listItemHoverBg = dt.tokens['color-action-background-standard-hover'];
      listItemFocusOutline = 'var(--focus-light-bg)';
    }
    return {
      '--list-item-fg': listItemFg,
      '--list-item-disabled-fg': dt.tokens['color-action-text-standard-disabled'],
      '--list-item-disabled-bg': 'transparent',
      '--list-item-secondary-fg': dt.tokens['color-label-text'],
      '--list-item-hover-bg': listItemHoverBg,
      '--list-item-hover-fg': listItemHoverFg,
      '--list-item-focus-outline': listItemFocusOutline,
      '--list-item-selected-bg': dt.tokens['color-action-background-standard-selected'],
      '--list-item-selected-fg': dt.tokens['color-action-text-standard-selected'],
      '--list-item-selected-focus-outline': dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : listItemFocusOutline,
      '--list-item-active-bg': dt.tokens['color-action-background-standard-selected'],
      '--list-item-active-fg': dt.tokens['color-action-text-standard-selected'],
      '--list-item-separator-bd': dt.tokens['color-container-border-4'],
      '--list-item-highlighted-bg': dt.reportTokens.text.highlighted.bg,
      '--list-item-highlighted-fg': dt.reportTokens.text.highlighted.fg,
      '--list-item-selected-focus-outline-offset': 'calc(-2px + var(--focus-width) * -1)'
    };
  }
  switch (dt.themeVariant) {
    case 'dark':
      return {
        '--list-item-fg': dt.c.white,
        '--list-item-disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        '--list-item-disabled-bg': 'transparent',
        '--list-item-secondary-fg': transparentize(0.2, dt.c.white),
        '--list-item-hover-bg': transparentize(0.9, dt.c.white),
        '--list-item-hover-fg': dt.c.white,
        '--list-item-focus-outline': 'var(--focus-light-bg)',
        '--list-item-selected-bg': transparentize(0.85, dt.c.white),
        '--list-item-selected-fg': dt.c.white,
        '--list-item-active-bg': transparentize(0.85, dt.c.white),
        '--list-item-active-fg': dt.reportTokens.stateStandard.active.fg,
        '--list-item-selected-filter': dt.reportTokens.stateStandard.selected.filter,
        '--list-item-active-filter': dt.reportTokens.stateStandard.active.filter,
        '--list-item-separator-bd': dt.c.white15,
        '--list-item-selected-focus-outline': 'var(--focus-dark-bg)'
      };
    case 'high-contrast':
      return {
        '--list-item-fg': dt.c.white,
        '--list-item-disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        '--list-item-disabled-bg': 'transparent',
        '--list-item-secondary-fg': dt.c.white,
        '--list-item-hover-bg': dt.reportTokens.stateStandard.hover.bg,
        '--list-item-hover-fg': dt.reportTokens.stateStandard.hover.fg,
        '--list-item-focus-outline': 'var(--focus-light-bg)',
        '--list-item-selected-bg': dt.reportTokens.stateStandard.selected.bg,
        '--list-item-selected-fg': dt.reportTokens.stateStandard.selected.fg,
        '--list-item-active-bg': dt.reportTokens.stateStandard.active.bg,
        '--list-item-active-fg': dt.reportTokens.stateStandard.active.fg,
        '--list-item-selected-filter': dt.reportTokens.stateStandard.selected.filter,
        '--list-item-active-filter': dt.reportTokens.stateStandard.active.filter,
        '--list-item-separator-bd': dt.c.white,
        '--list-item-selected-focus-outline': 'var(--focus-dark-bg)'
      };
    default:
      return {
        '--list-item-fg': dt.c.n_3,
        '--list-item-disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        '--list-item-disabled-bg': 'transparent',
        '--list-item-secondary-fg': dt.c.n_1,
        '--list-item-hover-bg': dt.reportTokens.stateStandard.hover.bg,
        '--list-item-hover-fg': dt.reportTokens.stateStandard.hover.fg,
        '--list-item-focus-outline': 'var(--focus-light-bg)',
        '--list-item-selected-bg': dt.reportTokens.stateStandard.selected.bg,
        '--list-item-selected-fg': dt.reportTokens.stateStandard.selected.fg,
        '--list-item-active-bg': dt.reportTokens.stateStandard.active.bg,
        '--list-item-active-fg': dt.reportTokens.stateStandard.active.fg,
        '--list-item-selected-filter': dt.reportTokens.stateStandard.selected.filter,
        '--list-item-active-filter': dt.reportTokens.stateStandard.active.filter,
        '--list-item-separator-bd': dt.c.n_3,
        '--list-item-selected-focus-outline': 'var(--focus-light-bg)'
      };
  }
};

// Transform names from tokens prop to more expected CSS property names
export const transformTokensToCSS = (index, tokens) => {
  let css = {};
  if (tokens) {
    const t = {};
    Object.entries(tokens(index).font ?? {}).forEach(([k, v]) => {
      const name = k.replace(/([A-Z])/g, match => `-${match.toLowerCase()}`);
      if (v && name) t[name] = v;
    });
    css = renderInlineCssVars(t, 'list-item');
  }
  return css;
};
//# sourceMappingURL=utils.js.map